<template>
  <b-modal
    v-model="showModal"
    title="Welcome to Customer Engine!"
    size="lg"
    no-close-on-backdrop
    no-close-on-esc
    hide-header-close
    centered
  >
    <div class="text-center mb-4">
      <div class="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style="width: 64px; height: 64px;">
        <i class="uil uil-rocket" style="font-size: 32px;"></i>
      </div>
      <h5 class="mb-2">Let's set up your account</h5>
      <p class="text-muted">
        Tell us a bit about yourself and your agency to get started with Customer Engine.
      </p>
    </div>

    <ValidationObserver v-slot="{ handleSubmit }" ref="setupForm">
      <form @submit.prevent="handleSubmit(submitSetup)">
        <!-- Personal Information -->
        <div class="mb-4">
          <h6 class="text-uppercase text-muted small mb-3">
            <i class="uil uil-user mr-1"></i> Your Information
          </h6>
          <div class="row">
            <div class="col-md-6">
              <TextInput 
                v-model="form.first_name" 
                name="first_name" 
                rules="required|min:2" 
                label="First Name"
                placeholder="John"
              />
            </div>
            <div class="col-md-6">
              <TextInput 
                v-model="form.last_name" 
                name="last_name" 
                rules="required|min:2" 
                label="Last Name"
                placeholder="Smith"
              />
            </div>
          </div>
        </div>

        <!-- Agency Information -->
        <div class="mb-4">
          <h6 class="text-uppercase text-muted small mb-3">
            <i class="uil uil-building mr-1"></i> Agency Information
          </h6>
          <div class="row">
            <div class="col-12">
              <TextInput 
                v-model="form.agency_name" 
                name="agency_name" 
                rules="required|min:2" 
                label="Agency Name"
                placeholder="My Marketing Agency"
              />
            </div>
          </div>
        </div>

        <!-- Subdomain (Optional) -->
        <div class="mb-4">
          <h6 class="text-uppercase text-muted small mb-3">
            <i class="uil uil-globe mr-1"></i> Your Portal URL <span class="text-muted font-weight-normal">(Optional)</span>
          </h6>
          <div class="form-group">
            <label for="subdomain">Subdomain</label>
            <div class="input-group">
              <input
                id="subdomain"
                v-model="form.subdomain"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': subdomainError }"
                placeholder="myagency"
                @input="handleSubdomainInput"
              />
              <div class="input-group-append">
                <span class="input-group-text">.broadcastengine.io</span>
              </div>
            </div>
            <small v-if="subdomainError" class="text-danger">{{ subdomainError }}</small>
            <small v-else class="text-muted">
              Leave blank to keep your current subdomain. Only lowercase letters, numbers, and hyphens allowed.
            </small>
          </div>
        </div>
      </form>
    </ValidationObserver>

    <template v-slot:modal-footer>
      <div class="w-100 d-flex justify-content-between align-items-center">
        <small class="text-muted">
          <i class="uil uil-info-circle"></i> 
          You can update these settings later from your profile.
        </small>
        <b-button 
          variant="primary" 
          :disabled="saving" 
          @click="submitSetup"
        >
          <b-spinner v-if="saving" small class="mr-1"></b-spinner>
          {{ saving ? 'Saving...' : 'Get Started' }}
          <i v-if="!saving" class="uil uil-arrow-right ml-1"></i>
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
export default {
  name: 'NitroWelcomeModal',

  data() {
    return {
      showModal: false,
      saving: false,
      subdomainError: '',
      form: {
        first_name: '',
        last_name: '',
        agency_name: '',
        subdomain: '',
      },
    }
  },

  computed: {
    user() {
      return this.$store.getters['auth/user']
    },
    agency() {
      return this.user?.agency
    },
  },

  mounted() {
    this.checkSetupRequired()
  },

  methods: {
    checkSetupRequired() {
      // Check if setup is required from localStorage
      const setupRequired = localStorage.getItem('nitro_setup_required')
      
      if (setupRequired === 'true' && this.user?.bundle_user_id) {
        // Pre-fill with current values
        this.form.first_name = this.user.first_name || ''
        this.form.last_name = this.user.last_name === 'User' ? '' : (this.user.last_name || '')
        this.form.agency_name = this.agency?.name || ''
        this.form.subdomain = '' // Leave blank by default
        
        this.showModal = true
      }
    },

    handleSubdomainInput(event) {
      // Sanitize subdomain input
      let value = event.target.value.toLowerCase()
      value = value.replace(/[^a-z0-9-]/g, '')
      this.form.subdomain = value
      
      // Validate
      if (value && value.length < 3) {
        this.subdomainError = 'Subdomain must be at least 3 characters'
      } else if (value && !/^[a-z]/.test(value)) {
        this.subdomainError = 'Subdomain must start with a letter'
      } else {
        this.subdomainError = ''
      }
    },

    async submitSetup() {
      const isValid = await this.$refs.setupForm.validate()
      if (!isValid) return
      
      if (this.subdomainError) return

      this.saving = true

      try {
        // Update user profile (include email to satisfy API validation)
        await this.$store.dispatch('auth/updateProfile', {
          first_name: this.form.first_name,
          last_name: this.form.last_name,
          email: this.user.email,
        })

        // Update agency if we have one
        if (this.agency) {
          // Build the full agency object with updated fields
          const agencyUpdate = {
            ...this.agency,
            name: this.form.agency_name,
          }
          
          // Only include subdomain if provided and valid
          if (this.form.subdomain && this.form.subdomain.length >= 3) {
            agencyUpdate.subdomain = this.form.subdomain
          }

          await this.$store.dispatch('agency/update', agencyUpdate)
        }

        // Clear the setup flag
        localStorage.removeItem('nitro_setup_required')
        
        // Refresh profile to get updated data
        await this.$store.dispatch('auth/getProfile')

        this.showModal = false
        
        // Show success toast
        this.$bvToast.toast('Your account has been set up successfully!', {
          title: 'Welcome to Customer Engine',
          variant: 'success',
          solid: true,
        })

      } catch (error) {
        console.error('[nitro-setup] Error saving setup:', error)
        
        // Handle specific errors
        if (error.response?.data?.errors?.subdomain) {
          this.subdomainError = error.response.data.errors.subdomain[0] || 'This subdomain is already taken'
        } else {
          this.$bvToast.toast(
            error.response?.data?.message || 'Failed to save your settings. Please try again.',
            {
              title: 'Error',
              variant: 'danger',
              solid: true,
            }
          )
        }
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.input-group-text {
  background-color: #f1f3f4;
  border-color: #ced4da;
  font-size: 0.9rem;
  color: #6c757d;
}
</style>
